import { useCallback, useRef } from 'react';
import { useAppDispatch } from 'store/hooks/useApp';
import valueChainMapApi from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { VALUE_CHAIN_MAP_ANALYSES_TAG } from 'store/tags/valueChainMap';
import {
  VC_AGENT_MONITORING_SESSION_EXECUTIONS_TAG,
  VC_AGENT_SESSION_BOM_TAG,
  VC_AGENT_SESSION_LIST_TAG,
  VC_AGENT_SESSION_RECORD_TAG,
  VC_AGENT_SESSION_SUPPLIER_DISCOVERY_TAG,
} from 'store/tags/valueChainSession';

import useOpenAgentSession from './useOpenAgentSession';
import useOpenImpactAnalysis from './useOpenImpactAnalysis';
import useOpenMonitoringSession from './useOpenMonitoringSession';

import type { TAiChatToolName, TAiChatToolOutput } from '../interfaces/IAiChatTools';
import type { IAiChatToolOutput } from '../interfaces/IAiChatTransportOptions';

/** A tool output narrowed to one tool, on the name the model called. */
type TToolOutputOf<NAME extends TAiChatToolName> = Omit<IAiChatToolOutput, 'output'> & {
  output: TAiChatToolOutput<NAME>,
};

/**
 * Narrows a tool output to one tool: `if (isToolOutput(event, 'startImpactAnalysis')) event.output.ok`.
 * Safe on the same fact as `readToolComponent`: the name says which shape the backend answered.
 */
const isToolOutput = <NAME extends TAiChatToolName>(
  toolOutput: IAiChatToolOutput,
  name: NAME,
): toolOutput is TToolOutputOf<NAME> => toolOutput.toolName === name;

/**
 * What a live tool output makes the app do, beyond rendering the part.
 *
 * Nine tools act on the app today, each on the map it answered with. A tool that opens a panel or
 * a modal navigates to the map first when the user is not on it.
 *
 * The impact analysis tools open the map's Impact Scenarios panel on the analysis.
 * `createImpactAnalysis` and `updateImpactAnalysis` wrote an analysis the panel also lists, so
 * the panel's cache is told to read again and the panel opens on the analysis, showing what the
 * chat just made or changed. `startImpactAnalysis` started a run: the panel opens on the
 * analysis and follows that run by `resultId`, as it follows one its own button started. An
 * action the FE does not know opens the panel and follows nothing; `'openPanel'` will say so once
 * the approval step ships.
 *
 * The agent session tools act on the session they answered with. The backend did the work:
 * `createAgentSession` and `updateAgentSessionUnits` wrote the session and its tabs,
 * `startAgentSearch` started the searches. The FE never calls an explore-ai route. The session's
 * cache is told to read again, so an open Sessions panel or modal shows the session as it is now.
 * Only `startAgentSearch` opens the session modal, whatever its action; the tabs then move with
 * the socket, as the modal's own do. A create or an update opens nothing: its row's button does.
 *
 * The monitoring tools work the same way on a monitoring session of any agent: the session list
 * and the session's executions are read again, so an open panel shows the new session, units or
 * execution. Only `startMonitoringSession` opens something: the session's results in the run
 * history panel, on the execution the start made, as its "View results" does. An action the FE
 * does not know opens the results and does nothing more. The FE never calls the start route.
 *
 * Fed by the stream, not by the rendered rows, so a stored part never acts: a chat reloaded or
 * switched to renders the same rows and opens nothing. Each call acts once, keyed on the call id,
 * whatever the stream does around it.
 *
 * @see [11.6 Screen actions](../../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
const useAiChatToolActions = (): (toolOutput: IAiChatToolOutput) => void => {
  const dispatch = useAppDispatch();
  const openImpactAnalysis = useOpenImpactAnalysis();
  const openAgentSession = useOpenAgentSession();
  const openMonitoringSession = useOpenMonitoringSession();
  const actedCallIds = useRef<Set<string>>(new Set());

  return useCallback((toolOutput: IAiChatToolOutput) => {
    if (actedCallIds.current.has(toolOutput.toolCallId)) {
      return;
    }

    const isSearchStart = isToolOutput(toolOutput, 'startAgentSearch');
    const isAgentSession = isSearchStart
      || isToolOutput(toolOutput, 'createAgentSession')
      || isToolOutput(toolOutput, 'updateAgentSessionUnits');

    if (isAgentSession && toolOutput.output.ok) {
      actedCallIds.current.add(toolOutput.toolCallId);

      const { valueChainMapId, sessionId, subType } = toolOutput.output;
      const sessionTagId = `${valueChainMapId}-${sessionId}`;

      dispatch(valueChainMapApi.util.invalidateTags([
        VC_AGENT_SESSION_LIST_TAG,
        { type: VC_AGENT_SESSION_RECORD_TAG, id: sessionTagId },
        { type: VC_AGENT_SESSION_SUPPLIER_DISCOVERY_TAG, id: sessionTagId },
        { type: VC_AGENT_SESSION_BOM_TAG, id: sessionTagId },
      ]));

      if (isSearchStart) {
        openAgentSession({ valueChainMapId, sessionId, subType });
      }

      return;
    }

    const isMonitoringStart = isToolOutput(toolOutput, 'startMonitoringSession');
    const isMonitoringSession = isMonitoringStart
      || isToolOutput(toolOutput, 'createMonitoringSession')
      || isToolOutput(toolOutput, 'updateMonitoringSessionUnits');

    if (isMonitoringSession && toolOutput.output.ok) {
      actedCallIds.current.add(toolOutput.toolCallId);

      const { valueChainMapId, sessionId } = toolOutput.output;

      dispatch(valueChainMapApi.util.invalidateTags([
        VC_AGENT_SESSION_LIST_TAG,
        { type: VC_AGENT_MONITORING_SESSION_EXECUTIONS_TAG, id: `${valueChainMapId}-${sessionId}` },
      ]));

      if (isMonitoringStart) {
        // Compared as a string: an action the FE does not know opens the results, on no execution.
        const isStarted = (toolOutput.output.action as string) === 'monitoringStarted';

        openMonitoringSession({
          valueChainMapId,
          sessionId,
          view: 'results',
          ...(isStarted ? { executionKey: toolOutput.output.executionKey } : {}),
        });
      }

      return;
    }

    const isWrite = isToolOutput(toolOutput, 'createImpactAnalysis')
      || isToolOutput(toolOutput, 'updateImpactAnalysis');
    const isStart = isToolOutput(toolOutput, 'startImpactAnalysis');

    if (!(isWrite || isStart) || !toolOutput.output.ok) {
      return;
    }

    actedCallIds.current.add(toolOutput.toolCallId);

    const { valueChainMapId, analysisId, name } = toolOutput.output;

    if (isWrite) {
      dispatch(valueChainMapApi.util.invalidateTags([
        { type: VALUE_CHAIN_MAP_ANALYSES_TAG, id: valueChainMapId },
        { type: VALUE_CHAIN_MAP_ANALYSES_TAG, id: `${valueChainMapId}-${analysisId}` },
      ]));
    }

    // The action is compared as a string: the type knows one today, and one the FE does not
    // know must open the panel without following anything.
    const isRunStarted = isStart && (toolOutput.output.action as string) === 'runStarted';

    openImpactAnalysis({
      valueChainMapId,
      analysisId,
      name,
      ...(isRunStarted ? { resultId: toolOutput.output.resultId } : {}),
    });
  }, [dispatch, openAgentSession, openImpactAnalysis, openMonitoringSession]);
};

export default useAiChatToolActions;
